import React, {
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo
} from 'react'
import PropTypes from 'prop-types'
import { PublicClientApplication } from '@azure/msal-browser'

import { config, authenticationParameters } from 'admin/msal-config'

import { AuthContext } from './contexts'

export const msal = new PublicClientApplication(config)

const redirectPromise = msal.handleRedirectPromise()

const debugAuth = false
function debug(...args) {
  if (debugAuth && process.env.NODE_ENV === 'development') {
    console.debug('[getToken]', ...args)
  }
}

export function AuthWrapper(props) {
  const getToken = useCallback(async () => {
    const result = await redirectPromise

    debug('[getToken] Invoked')
    const account = result?.account ?? msal.getAllAccounts()[0]
    try {
      if (!account) throw 'NO_ACCOUNT'

      debug('[getToken] Account Found')
      const auth = await msal.acquireTokenSilent({
        ...authenticationParameters,
        forceRefresh: false,
        account
      })

      debug('[getToken] OK')
      return auth.accessToken
    } catch (e) {
      debug('[getToken] Error Caught', e)

      await msal.acquireTokenRedirect({
        ...authenticationParameters,
        account
      })

      await msal.handleRedirectPromise()

      return null
    }
  }, [])

  const [ready, setReady] = useState(false)
  useEffect(() => {
    let cancelled = false

    async function run() {
      debug('[getToken] Fetching Initial Token')
      await getToken()

      if (!cancelled) {
        setReady(true)
      }
    }

    // MSAL is just... the worst
    // Stop a race condition by only acquirring tokens after MSAL has flushed its processing behind the scenes
    setTimeout(() => {
      run()
    }, 1000)

    return () => {
      cancelled = true
    }
  }, [getToken])

  const auth = useMemo(() => {
    return {
      ready,
      getToken
    }
  }, [getToken, ready])

  return <AuthContext.Provider {...props} value={auth} />
}

AuthWrapper.propTypes = {
  children: PropTypes.node.isRequired
}

/**
 * @returns {{ 
    ready: boolean, 
    getToken: () => Promise<string>
    }} auth
 */
export function useAuth() {
  return useContext(AuthContext)
}
